import type { BaseSelectionItem } from "@trydig/centra-types/selection";
import { getUserData } from "@/features/global/user_data";
import type { PricelistItem, ProductCard, ProductDetails } from "../centra";
import type { GTMEventData, GTMProduct, PageView } from "./types";

export const selectionItemToGTMProduct = ({
	item,
	index,
	currency,
}: {
	item: BaseSelectionItem;
	index?: number;
	currency: string;
}): GTMProduct => {
	const categories = item.product?.categoryName
		? Object.fromEntries(
				item.product.categoryName
					.map((name, i) => [`item_category${i === 0 ? "" : i + 1}`, name])
					.slice(0, 4),
			)
		: {};

	return {
		item_id: item.product?.sku || "",
		item_name: item.product?.name || "",
		price: Math.floor(item.product?.priceAsNumber ?? 0),
		affiliation: "Website",
		currency,
		discount: Math.ceil(
			(item.product?.priceBeforeDiscountAsNumber ?? 0) -
				(item.product?.priceAsNumber ?? 0),
		),
		item_brand: item.product?.brandName,
		item_category: item.product?.categories?.at(-1)?.name?.join(","),
		...categories,
		index,
		item_variant: item.product?.variantName,
		quantity: item.quantity,
	};
};

export const selectionItemsToGTMProducts = ({
	items,
	currency,
}: {
	items: BaseSelectionItem[];
	currency: string;
}): GTMProduct[] => {
	return items.map((item, index) =>
		selectionItemToGTMProduct({
			item,
			currency,
			index,
		}),
	);
};

export const productDetailsToGTMProduct = ({
	product,
	price,
	currency,
}: {
	product: ProductDetails;
	price: PricelistItem;
	currency: string;
}): GTMProduct => {
	const categories = product.categoryName
		? Object.fromEntries(
				product.categoryName
					.map((name, i) => [`item_category${i === 0 ? "" : i + 1}`, name])
					.slice(0, 4),
			)
		: {};

	return {
		quantity: 1,
		item_id: product.sku || "",
		item_name: product.name || "",
		price: Math.floor(price?.priceAsNumber ?? 0),
		affiliation: "Website",
		currency,
		discount: Math.ceil(
			(price?.priceBeforeDiscountAsNumber ?? 0) - (price?.priceAsNumber ?? 0),
		),
		item_brand: product.brand,
		item_category: product.categories?.at(-1)?.name?.join(","),
		...categories,
		item_variant: product.variantName,
		item_image_url: product.media.at(0)?.href,
	};
};

export const productCardsToGTMProducts = ({
	products,
	currency,
	categoryPage,
}: {
	products: ProductCard[];
	currency: string;
	categoryPage: string;
}): GTMProduct[] => {
	return products.map((product, index) =>
		productCardToGTMProduct({ product, index, currency, categoryPage }),
	);
};

export const productCardToGTMProduct = ({
	product,
	index,
	currency,
	categoryPage,
}: {
	product: ProductCard;
	index: number;
	currency: string;
	categoryPage: string;
}): GTMProduct => {
	const categories = product.categories
		? Object.fromEntries(
				product.categories
					.map((name, i) => [`item_category${i === 0 ? "" : i + 1}`, name.uri])
					.slice(0, 4),
			)
		: {};

	return {
		quantity: 1,
		item_id: product.sku || "",
		item_name: product.name || "",
		price: Math.floor(product?.priceAsNumber ?? 0),
		affiliation: "Website",
		currency,
		discount: Math.ceil(
			(product?.priceBeforeDiscountAsNumber ?? 0) -
				(product?.priceAsNumber ?? 0),
		),
		item_brand: product.brand,
		item_category: product.categories?.at(-1)?.name?.join(","),
		...categories,
		index,
		item_variant: product.variantName,
		item_list_name: categoryPage,
		item_image_url: product.media.at(0)?.href,
	};
};

export const sendGTMEvent = (data: GTMEventData | PageView) => {
	if (typeof window === "undefined") return;

	window.dataLayer ||= [];

	window.dataLayer.push({
		ecommerce: null,
	});
	const userData = getUserData();
	const payload = userData ? { ...data, user_data: userData } : data;
	window.dataLayer.push(payload);
};
